import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Zap, Sparkles, MessageCircle, Check, Loader2 } from 'lucide-react';

/**
 * Store — one-time purchases for Meet Priority (boost), Serious Interest
 * (super_like) and Warm Intro (intro).
 *
 * Feb 2026 — visual language aligned with the Subscription page: cream
 * background, editorial header, category toggle pills, elevated cards
 * per SKU with a "Most Popular" badge on the mid-tier bundle. Purchase
 * kicks off a Stripe Checkout session and returns to /store with a
 * session_id we poll to confirm fulfilment.
 */

const CATEGORIES = [
  {
    key:         'warm_intro',
    label:       'Warm Intros',
    accent:      '#1E4FD8',      // indigo — matches the intros badge on Discover
    Icon:        MessageCircle,
    unitLabel:   'Warm Intro',
    unitPlural:  'Warm Intros',
    invField:    'purchased_intros',
    tagline:     'DM someone you haven\u2019t matched with yet. Stacks on top of your daily allowance.',
  },
  {
    key:         'serious_interest',
    label:       'Serious Interests',
    accent:      '#B38138',      // amber — same accent as super-likes UI
    Icon:        Sparkles,
    unitLabel:   'Serious Interest',
    unitPlural:  'Serious Interests',
    invField:    'purchased_super_likes',
    tagline:     'Signal intentional interest. Sends a super-like so they see you first.',
  },
  {
    key:         'meet_priority',
    label:       'Meet Priority',
    accent:      '#B91C1C',      // brand red — same accent as boost UI
    Icon:        Zap,
    unitLabel:   'Meet Priority',
    unitPlural:  'Meet Priority',   // no naive "s" pluralization — reads as a name
    invField:    'purchased_boosts',
    tagline:     '60 minutes at the top of Discover. Use during peak hours for the best reach.',
  },
];

export default function Store() {
  const navigate = useNavigate();
  const [products, setProducts] = useState({ meet_priority: [], serious_interest: [], warm_intro: [] });
  const [inventory, setInventory] = useState({ purchased_boosts: 0, purchased_super_likes: 0, purchased_intros: 0 });
  const [category, setCategory] = useState('warm_intro');
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/store/products').then(r => setProducts(r.data || {})),
      api.get('/store/inventory').then(r => setInventory(r.data || {})),
    ]).catch(() => {}).finally(() => setLoading(false));

    const params = new URLSearchParams(window.location.search);
    const sid = params.get('session_id');
    if (sid) {
      setPolling(true);
      pollStoreStatus(sid, 0);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const pollStoreStatus = async (sid, attempt) => {
    if (attempt >= 6) { setPolling(false); toast.error('Purchase status check timed out'); return; }
    try {
      const r = await api.get(`/store/checkout/status/${sid}`);
      if (r.data.payment_status === 'paid') {
        toast.success('Purchase complete \u2014 credits added.');
        // Refresh inventory
        try {
          const inv = await api.get('/store/inventory');
          setInventory(inv.data || {});
        } catch { /* ignore — user can pull to refresh */ }
        setPolling(false);
        return;
      }
    } catch { /* transient — keep polling */ }
    setTimeout(() => pollStoreStatus(sid, attempt + 1), 1500);
  };

  const buy = async (productKey) => {
    setBuying(productKey);
    try {
      const r = await api.post('/store/checkout', {
        product_key: productKey,
        origin_url:  window.location.origin,
      });
      if (r.data?.url) {
        window.location.href = r.data.url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout failed');
    } finally {
      setBuying(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
        <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
      </div>
    );
  }

  const activeCat = CATEGORIES.find(c => c.key === category) || CATEGORIES[0];
  const bundles = products[activeCat.key] || [];
  const inventoryValue = inventory[activeCat.invField] || 0;
  const CatIcon = activeCat.Icon;

  // Sort bundles by quantity ASC — 1, 5, 15, 30
  const sortedBundles = [...bundles].sort((a, b) => (a.qty || 0) - (b.qty || 0));
  // "Most Popular" = the bundle carrying a `Most popular` badge (server-side).
  const popularIdx = sortedBundles.findIndex(b => (b.badge || '').toLowerCase().includes('popular'));

  return (
    <div className="min-h-screen bg-[var(--cream)] px-6 py-8 pb-28" data-testid="store-page">
      <div className="max-w-lg mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#4B4B52] hover:text-[#0F0F10] mb-8 text-sm"
          data-testid="store-back"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <p className="text-[11px] tracking-[0.2em] uppercase text-[#B91C1C] font-semibold mb-2">Add-ons</p>
        <h1 className="font-display-tight text-[26px] sm:text-3xl font-[600] tracking-tight mb-3 leading-[1.15]">
          Boost your <em className="italic font-[500] brand-gradient-text">reach</em>
        </h1>
        <p className="text-[#4B4B52] text-sm mb-8">One-time purchases. No renewals. Use them any time.</p>

        {/* Category toggle — same visual as Subscription's billing pills */}
        <div
          className="flex items-center gap-1 p-1 rounded-full bg-black/[0.04] border border-[var(--hairline)] mb-6 overflow-x-auto no-scrollbar"
          data-testid="store-category-toggle"
        >
          {CATEGORIES.map(cat => {
            const active = category === cat.key;
            const CatI = cat.Icon;
            return (
              <button
                key={cat.key}
                onClick={() => setCategory(cat.key)}
                data-testid={`store-cat-${cat.key}`}
                className={`shrink-0 px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all inline-flex items-center gap-2 ${
                  active ? 'bg-white shadow-sm text-[#0F0F10]' : 'text-[#4B4B52] hover:text-[#0F0F10]'
                }`}
              >
                <CatI size={13} style={active ? { color: cat.accent } : undefined} />
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* Inventory / tagline strip for the active category */}
        <div
          className="rounded-2xl px-4 py-3 mb-6 flex items-center gap-3"
          style={{
            background: `${activeCat.accent}0D`,
            border: `1px solid ${activeCat.accent}22`,
          }}
          data-testid="store-inventory-strip"
        >
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${activeCat.accent}18`, border: `1px solid ${activeCat.accent}30` }}
          >
            <CatIcon size={16} style={{ color: activeCat.accent }} strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12.5px] text-[#4B4B52] leading-snug">{activeCat.tagline}</p>
          </div>
          <div className="text-right flex-shrink-0" data-testid={`store-inventory-${activeCat.key}`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8A8A93]">You have</p>
            <p className="font-display-tight text-[20px] font-[700] leading-none" style={{ color: activeCat.accent }}>
              {inventoryValue}
            </p>
          </div>
        </div>

        {/* Bundle cards */}
        <div className="space-y-4" data-testid="store-bundle-list">
          {sortedBundles.map((bundle, i) => {
            const isPopular = i === popularIdx;
            const unit = (bundle.price / (bundle.qty || 1)).toFixed(2);
            return (
              <div
                key={bundle.key}
                className={`relative rounded-2xl p-6 transition-all premium-card ${isPopular ? 'elevated-card border-2' : ''}`}
                style={isPopular ? { borderColor: `${activeCat.accent}40` } : undefined}
                data-testid={`store-bundle-${bundle.key}`}
              >
                {isPopular && (
                  <div
                    className="absolute -top-3 right-6 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.12em] text-white"
                    style={{ background: `linear-gradient(135deg, ${activeCat.accent}, ${activeCat.accent}CC)` }}
                  >
                    Most Popular
                  </div>
                )}

                <div className="flex items-start justify-between mb-5">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center"
                      style={{ background: `${activeCat.accent}12`, border: `1px solid ${activeCat.accent}28` }}
                    >
                      <CatIcon size={20} style={{ color: activeCat.accent }} strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="font-display font-[600] text-xl tracking-tight">
                        {bundle.qty} {bundle.qty > 1 ? activeCat.unitPlural : activeCat.unitLabel}
                      </h3>
                      <div className="flex items-baseline gap-1 mt-0.5">
                        <span className="font-display-tight text-2xl font-[600] text-[#0F0F10]">${Number(bundle.price).toFixed(2)}</span>
                        <span className="text-xs text-[#8A8A93]">one-time</span>
                      </div>
                      <p className="text-[11px] text-[#B38138] mt-0.5 font-medium">${unit} per {activeCat.unitLabel.toLowerCase()}</p>
                    </div>
                  </div>
                  {bundle.badge && !isPopular && (
                    <span
                      className="text-[10px] font-bold tracking-wider uppercase px-2.5 py-1 rounded-full"
                      style={{ background: `${activeCat.accent}14`, color: activeCat.accent, border: `1px solid ${activeCat.accent}30` }}
                    >
                      {bundle.badge}
                    </span>
                  )}
                </div>

                <ul className="space-y-2.5 mb-5">
                  <li className="flex items-center gap-2.5 text-sm text-[#4B4B52]">
                    <Check size={14} style={{ color: activeCat.accent }} className="flex-shrink-0" strokeWidth={2.5} />
                    {bundle.desc || `Bundle of ${bundle.qty}`}
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-[#4B4B52]">
                    <Check size={14} style={{ color: activeCat.accent }} className="flex-shrink-0" strokeWidth={2.5} />
                    No expiration
                  </li>
                  <li className="flex items-center gap-2.5 text-sm text-[#4B4B52]">
                    <Check size={14} style={{ color: activeCat.accent }} className="flex-shrink-0" strokeWidth={2.5} />
                    Instant credit after payment
                  </li>
                </ul>

                <button
                  onClick={() => buy(bundle.key)}
                  disabled={!!buying || polling}
                  data-testid={`store-buy-${bundle.key}`}
                  className={`btn-shine w-full py-3.5 rounded-full font-semibold text-sm transition-all active:scale-[0.98] text-white ${
                    isPopular ? 'brand-glow-sm hover:scale-[1.01]' : ''
                  }`}
                  style={{
                    background: `linear-gradient(135deg, ${activeCat.accent}, ${activeCat.accent}CC)`,
                    boxShadow: isPopular ? `0 6px 18px -4px ${activeCat.accent}55` : undefined,
                  }}
                >
                  {buying === bundle.key
                    ? <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                    : `Buy ${bundle.qty} · $${Number(bundle.price).toFixed(2)}`}
                </button>
              </div>
            );
          })}
        </div>

        {polling && (
          <p className="text-center text-[#B38138] text-xs mt-6 flex items-center justify-center gap-2" data-testid="store-poll-status">
            <Loader2 size={12} className="animate-spin" /> Confirming your purchase…
          </p>
        )}
        <p className="text-center text-[#8A8A93] text-xs mt-8">
          One-time purchases don&rsquo;t renew. Credits never expire.
          <br />
          <span className="text-[#B38138] font-medium">Prefer everything unlimited? Upgrade to VIP.</span>
        </p>
      </div>
    </div>
  );
}
